import React, { useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { 
  useGetStudentRequestStatusesQuery,
  useAddStudentRequestStatusMutation,
  useUpdateStudentRequestStatusMutation,
  useDeleteStudentRequestStatusMutation
} from '../store/api';
import { studentRequestStatuses as mockStatuses } from '../data/mockData';

export default function RequestStatuses() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '#3B82F6' });

  // API hooks
  const { data: apiResponse, isLoading, error } = useGetStudentRequestStatusesQuery();
  
  // Handle different API response formats
  const statuses = React.useMemo(() => {
    // If there's an error or no API response, use mock data as fallback
    if (error || !apiResponse) {
      console.log('API not available, using mock data for statuses');
      return mockStatuses;
    }
    
    // If API returns an array directly
    if (Array.isArray(apiResponse)) {
      return apiResponse;
    }
    
    // If API returns an object with data property
    if (apiResponse.data && Array.isArray(apiResponse.data)) {
      return apiResponse.data;
    }
    
    // If API returns an object with results property
    if (apiResponse.results && Array.isArray(apiResponse.results)) {
      return apiResponse.results;
    }
    
    // If API response is not in expected format, use mock data
    console.log('API response format unexpected, using mock data for statuses');
    return mockStatuses;
  }, [apiResponse, error]);
  const [addStatus, { isLoading: isAdding }] = useAddStudentRequestStatusMutation();
  const [updateStatus, { isLoading: isUpdating }] = useUpdateStudentRequestStatusMutation();
  const [deleteStatus, { isLoading: isDeleting }] = useDeleteStudentRequestStatusMutation();

  const handleAdd = () => {
    setEditingStatus(null);
    setFormData({ name: '', color: '#3B82F6' });
    setIsModalOpen(true);
  };

  const handleEdit = (status) => {
    setEditingStatus(status);
    setFormData({ name: status.name, color: status.color });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStatus) {
        await updateStatus({ id: editingStatus.id, obj: formData }).unwrap();
      } else {
        await addStatus(formData).unwrap();
      }
      setIsModalOpen(false);
      setFormData({ name: '', color: '#3B82F6' });
    } catch (error) {
      console.error('Error saving status:', error);
      // If API is not available, show a different message
      if (error.status === 'FETCH_ERROR' || error.status === 404) {
        alert('API mövcud deyil. Məlumat yalnız səhifə yeniləndikdə mövcud olacaq.');
      } else {
        alert('Xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.');
      }
    }
  };

  const handleDelete = async (statusId, statusName) => {
    if (window.confirm(`"${statusName}" statusunu silmək istədiyinizə əminsiniz?`)) {
      try {
        await deleteStatus(statusId).unwrap();
      } catch (error) {
        console.error('Error deleting status:', error);
        // If API is not available, show a different message
        if (error.status === 'FETCH_ERROR' || error.status === 404) {
          alert('API mövcud deyil. Məlumat yalnız səhifə yeniləndikdə mövcud olacaq.');
        } else {
          alert('Xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.');
        }
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Only show error if we don't have fallback data
  if (error && (!statuses || statuses.length === 0)) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Xəta baş verdi</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>Statusları yükləyərkən xəta baş verdi. Zəhmət olmasa səhifəni yeniləyin.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Müraciət Statusları</h1>
          <p className="mt-1 text-sm text-gray-600">Tələbə müraciətlərinin statuslarını idarə edin</p>
        </div>
        <button 
          onClick={handleAdd}
          disabled={isAdding}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          {isAdding ? 'Əlavə edilir...' : 'Yeni Status'}
        </button>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {statuses.map((status) => (
              <div key={status.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: status.color }}
                    />
                    <span className="text-sm font-medium text-gray-900">{status.name}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleEdit(status)}
                      disabled={isUpdating}
                      className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed">
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={() => handleDelete(status.id, status.name)}
                      disabled={isDeleting}
                      className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed">
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-2">
                  <span 
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: status.color + '20', 
                      color: status.color 
                    }}
                  >
                    {status.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsModalOpen(false)} />
            <div className="bg-white rounded-lg p-6 max-w-md w-full relative">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingStatus ? 'Statusu Redaktə Et' : 'Yeni Status Əlavə Et'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status Adı *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Məsələn: Yeni müraciət"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rəng *</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="h-10 w-16 border border-gray-300 rounded-md"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button 
                    type="button" 
                    onClick={() => setIsModalOpen(false)} 
                    disabled={isAdding || isUpdating}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    Ləğv et
                  </button>
                  <button 
                    type="submit" 
                    disabled={isAdding || isUpdating}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                    {isAdding || isUpdating ? 'Yadda saxlanılır...' : (editingStatus ? 'Yenilə' : 'Yadda saxla')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 